import type { AiModelEnum, Annotation, EvidenceSessionAIModel } from 'api';
import { Box, Checkbox, Typography, useBreakpoint } from 'ui';

import {
  getGroupedAnnotations,
  type GroupedAnnotation,
  isGroupHidden,
} from '../../utils/formatAnnotations/formatAnnotations';

const SegmentationLegendItem = ({
  label,
  colour,
  hidden,
  handleClick,
}: {
  label: string;
  colour: readonly number[];
  hidden: boolean;
  handleClick: () => void;
}) => {
  const rgbColour = `rgb(${colour[0]}, ${colour[1]}, ${colour[2]})`;
  const dataTestId = hidden ? 'segmentation-legend-icon-hollow' : 'segmentation-legend-icon';
  const { isMobile } = useBreakpoint();

  return (
    <Box
      display="flex"
      alignItems="center"
      mb={isMobile ? 1 : 0}
      sx={{
        ':hover': {
          backgroundColor: '#F9F9F9',
        },
        backgroundColor: hidden ? '#FFFFFF' : '#6347FF0D',
        border: 1,
        borderColor: hidden ? '#0D0C1D1A' : '#6347FF',
        borderRadius: 2,
        cursor: 'pointer',
      }}
      onClick={handleClick}
    >
      <Checkbox checked={!hidden} />
      <Box
        width="20px"
        height="20px"
        bgcolor={rgbColour}
        borderRadius={1}
        border={rgbColour}
        mr={1}
        data-testid={dataTestId}
      />
      <Typography variant="caption" fontWeight="medium" noWrap={true} mr={2}>
        {label}
      </Typography>
    </Box>
  );
};

export const SegmentationLegend = ({
  annotations,
  hiddenCategories,
  onSelectCategory,
  model,
  showAerosols = false,
}: {
  annotations: Annotation[];
  hiddenCategories: string[];
  onSelectCategory: (label: string) => void;
  model: AiModelEnum | EvidenceSessionAIModel;
  showAerosols?: boolean;
}) => {
  const { isMobile } = useBreakpoint();
  const groupedAnnotations = getGroupedAnnotations(annotations, model, showAerosols);

  const handleGroupClick = (groupedItem: GroupedAnnotation) => {
    const isHidden = isGroupHidden(groupedItem, hiddenCategories);

    for (const label of groupedItem.originalLabels) {
      const isLabelCurrentlyHidden = hiddenCategories.includes(label);

      if ((isHidden && isLabelCurrentlyHidden) || (!isHidden && !isLabelCurrentlyHidden)) {
        onSelectCategory(label);
      }
    }
  };

  return (
    <Box
      display="flex"
      flexDirection={isMobile ? 'row' : 'column'}
      paddingX={isMobile ? 0 : 4}
      marginX={isMobile ? 3 : 0}
      gap={1}
      justifyContent={isMobile ? 'flex-start' : 'center'}
      data-testid="segmentation-legend"
      overflow="auto"
    >
      {groupedAnnotations.map((group) => (
        <SegmentationLegendItem
          key={group.groupName}
          label={group.groupName}
          colour={group.colour}
          hidden={isGroupHidden(group, hiddenCategories)}
          handleClick={() => handleGroupClick(group)}
        />
      ))}
    </Box>
  );
};
